import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { runtimeConfig } from "./lib/runtime-config";

const RESIZE_OBSERVER_MSG = "ResizeObserver loop";

// ── Channel 1: capturing error event listener ─────────────────────────────
// Fires before window.onerror and all non-capturing listeners, so it
// intercepts the benign browser ResizeObserver notification before Plivo's
// window.onerror handler sees it and re-throws it as a non-Error crash.
window.addEventListener(
  "error",
  (e) => {
    if (e.message?.includes(RESIZE_OBSERVER_MSG)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true // capture phase
);

// ── Channel 2: wrap window.onerror via Object.defineProperty ─────────────
// Plivo's SDK assigns window.onerror lazily (after our code runs), so we
// intercept the assignment itself and bake our filter into whatever handler
// Plivo (or anyone else) sets, no matter when it happens.
let _storedOnerror: typeof window.onerror = null;
try {
  Object.defineProperty(window, "onerror", {
    configurable: true,
    get: () => _storedOnerror,
    set: (fn: typeof window.onerror) => {
      _storedOnerror = fn
        ? (msg, src, line, col, err) => {
            if (typeof msg === "string" && msg.includes(RESIZE_OBSERVER_MSG)) {
              return true; // handled — suppress
            }
            return fn(msg, src, line, col, err) ?? false;
          }
        : fn;
    },
  });
} catch {
  // Some browsers disallow redefining window.onerror — fall back gracefully.
}

// ── Sentry init — only when DSN is provided ───────────────────────────────
const sentryDsn = runtimeConfig.sentryDsn;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: runtimeConfig.environment,
    release: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "unknown",
    // Capture 100 % of errors (not traces).
    sampleRate: 1.0,
    beforeSend(event) {
      // Drop benign ResizeObserver noise so it never pollutes the Sentry project.
      const msg =
        event.exception?.values?.[0]?.value ?? event.message ?? "";
      if (typeof msg === "string" && msg.includes(RESIZE_OBSERVER_MSG)) {
        return null;
      }
      return event;
    },
  });

  // ── Global unhandled-rejection capture ──────────────────────────────────
  // Sentry's SDK registers this automatically, but we add an explicit listener
  // so we can skip non-actionable promise rejections (e.g. expected network
  // cancellations) before they reach Sentry.
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    // Skip: aborted fetch / cancelled requests (not bugs).
    if (reason instanceof DOMException && reason.name === "AbortError") return;
    if (reason instanceof TypeError && reason.message?.includes("NetworkError")) return;
  });
}

// ── Error fallback shown by the application Error Boundary ───────────────
function AppErrorFallback() {
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h2 style={{ marginBottom: "0.5rem" }}>Something went wrong</h2>
      <p style={{ color: "#666", marginBottom: "1rem" }}>
        An unexpected error occurred. Reload the page to try again.
      </p>
      <button onClick={() => window.location.reload()} style={{ padding: "0.5rem 1rem", cursor: "pointer" }}>
        Reload page
      </button>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
    <App />
  </Sentry.ErrorBoundary>,
);
